// Copyright 2024 Sorama B.V.
import { Range } from "datatypes/SoramaLocalApi";

export class RangeLogic {
    /**
     * Subtract a collection of ranges from a bounding range
     * We assume that the provided ranges are non-overlapping and from < to
     * Ranges can be in random order
     * @param bound Bounding range (what to subtract from)
     * @param ranges Ranges to subtract
     * @returns Collection of ranges that are not in the ranges collection
     * @example
     * [0,10] - [3, 6] = [0,3] + [6,10]
     * [0,10] - [5,15] = [0,5]
     * [0,10] - [10,20] = [0,10]
     */
    static subtract(bound: Range, ranges: Range[]): Range[] {
        const remainingRanges: Range[] = [];

        // Copy ranges and the sort ranges on from property
        const sortedRanges = ranges.slice().sort((a, b) => a.from - b.from);

        // Remove ranges fully outside of bounds (only keep ranges that overlap the bound)
        const sortedRangesInBounds = sortedRanges.filter((range) => range.to >= bound.from && range.from <= bound.to);

        if (sortedRangesInBounds.length === 0) return [bound];

        let remainingRangeStart = bound.from;
        for (const rangeToSubtract of sortedRangesInBounds) {
            const rangeOverlapsStart = rangeToSubtract.from < bound.from && rangeToSubtract.to >= bound.from;
            const rangeOverlapsEnd = rangeToSubtract.from <= bound.to && rangeToSubtract.to > bound.to;

            // Create a remaining range
            // If the range is fully inside the bound and there is a gap after the previous range
            // Or if the range only overlaps the end of the bound and the start is inside the bounds
            const rangeIsFullyInsideBoundWithGap =
                !rangeOverlapsStart && !rangeOverlapsEnd && rangeToSubtract.from > remainingRangeStart;
            const rangeOnlyOverlapsEnd = rangeOverlapsEnd && rangeToSubtract.from > remainingRangeStart;
            if (rangeIsFullyInsideBoundWithGap || rangeOnlyOverlapsEnd) {
                remainingRanges.push({ from: remainingRangeStart, to: rangeToSubtract.from });
            }

            // Start remaining range at the end of range to subtract
            remainingRangeStart = rangeToSubtract.to;
        }

        // Add final remaining range
        if (remainingRangeStart < bound.to) {
            remainingRanges.push({ from: remainingRangeStart, to: bound.to });
        }

        return remainingRanges;
    }

    /**
     * Union a collection of ranges / merge overlapping ranges.
     * @param ranges Collection of ranges to union
     * @param additionalMergeOperation Optional function to set properties except "from" and "to" in a custom way (e.g. to merge selectable ranges)
     * @returns A new collection of ranges where overlapping ranges are merged
     * @example
     * [[0,10], [5,15]] = [[0,15]]
     * [[0,10], [10,20]] = [[0,20]]
     * [[0,10], [15,20]] = [[0,10], [15,20]]
     */
    static union<T extends Range>(ranges: T[], additionalMergeOperation?: (a: T, b: T, merged: T) => void): T[] {
        if (ranges.length == 0) return [];

        // Sort ranges by their start
        const sortedRanges = ranges.slice().sort((a, b) => a.from - b.from);
        const mergedRanges = [];

        // For each range, we try to increase it when there are other ranges after that overlap it
        let currentRange = sortedRanges[0];
        for (const range of sortedRanges) {
            const nextRange = range;

            // If the current range overlaps the next range, set its end to the max of the two ends
            // This is needed for the case where the next range is fully inside the current range
            if (currentRange.to >= nextRange.from) {
                const currentRangeBeforeMerge = { ...currentRange };
                currentRange.to = Math.max(currentRange.to, nextRange.to);

                if (additionalMergeOperation) {
                    additionalMergeOperation(currentRangeBeforeMerge, nextRange, currentRange);
                }
            } else {
                // If not, there is a gap between the current and next range and we can start by increasing the next range
                mergedRanges.push(currentRange);
                currentRange = nextRange;
            }
        }

        mergedRanges.push(currentRange);

        return mergedRanges;
    }

    /**
     * Test if there exists any overlap between the provided ranges
     * @param range Collection of ranges to test
     * @returns True if there is any overlap between any of the ranges
     * @example
     * [[0,10], [5,15]] = true
     * [[0,10], [10,20]] = false
     */
    static hasOverlap(ranges: Range[]): boolean {
        const sortedRanges = ranges.slice().sort((a, b) => a.from - b.from);
        for (let i = 1; i < sortedRanges.length; i++) {
            if (sortedRanges[i].from < sortedRanges[i - 1].to) {
                return true;
            }
        }

        return false;
    }

    /**
     * Find the first index in "ranges" that overlaps the test range
     * @param ranges Collection of ranges to test against
     * @param testRange Range to test
     * @returns The index of the first range that overlaps the test range
     */
    static findRangeHitIndex(ranges: Range[], testRange: Range): number {
        return ranges.findIndex((range) => testRange.to > range.from && testRange.from < range.to);
    }
}
